/**
 * @module Core
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:no-any*/ // Need to support all data types
import { HttpResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { SETTINGS, KEYPAIR } from '@environment/settings';
import { WorkerService } from '@core/worker/services/worker.service';
import { HeadersBuilderFactory } from '@core/api/utils/headers-builder-factory';
import { CommonHeadersBuilder } from '@core/api/utils/common-headers-builder';
import { WorkerRequestArgs } from '@core/worker/models/worker-request-args';
import { HttpEventInterceptorService } from '@core/error-handler/services/http-event-interceptor.service';
import { AppConfig } from '@root/app.config';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { ApiCache } from '@support/shared/interfaces/api-cache-object.interface';
import { EndpointConfigInterface } from '@core/interfaces/endpoint-configuration.interface';
import { API_GATEWAY_CONSTANTS } from '@core/api/constants/api-gateway-constants';
import { ApiProperties } from '@support/api/common/error.interface';
import { GlobalErrorService } from '@support/shared/widgets/services/global-error.service';
import { APIError } from '@support/api/common/error.interface';
import { SeoService } from '@core/services/seo.service';
import { Content } from '@core/aem/models/content.interface';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { Base64URL } from '@support/shared/services/encoding-utils';
import { CryptoSignatureService } from '@support/shared/services/crypto-signature.service';
import { map } from 'rxjs/operators';
import { WEB_API_MAP } from '@core/api/proxies/web-api-endpoints';

// @TODO is this a public service?
// @TODO add documentation
@Injectable()
export class ApiGatewayService {
  private spinnerOfDeathCounter: object;
  private spinnerOfDeathError: object;

  constructor(
    private workerService: WorkerService,
    private appConfig: AppConfig,
    private stateStoreUtil: StateStoreUtilsService,
    private commonUtil: CommonUtilService,
    private globalErrorService: GlobalErrorService,
    private httpEventInterceptorService: HttpEventInterceptorService,
    private seoService: SeoService,
    private objectService: ObjectService,
    private cryptoSignatureService: CryptoSignatureService
  ) {
    this.spinnerOfDeathCounter = {};
    this.spinnerOfDeathError = {};
  }

  /**
   * Performs any type of http get request to the worker service. First argument is required,
   * Second argument is optional, which is angular http Headers. THird parameter is object which is key value for
   * query params. Fourth argument is Object which is key value for path parameters in URL . fifth argument is
   * type of query string which is getting appended in endPoint in the url
   * Returns Observable for Response
   */

  public getRequest(
    endpointConfig: EndpointConfigInterface,
    headers: HttpHeaders = null,
    // TODO: Fix for Number in queryParams parameter
    // tslint:disable-next-line:no-any
    queryParams: any = null,
    // TODO: Fix for Number in pathParams and queryString parameter
    // tslint:disable-next-line:no-any
    pathParams: any = null,
    // TODO: Fix for Number in pathParams and queryString parameter
    // tslint:disable-next-line:no-any
    queryString: any = null,
    cacheObj?: ApiCache,
    critical: boolean = true,
    userFriendlyMessage?: string
  ): Observable<HttpResponse<Object>> {
    // TODO: CacheKey has to be sent dynamically. Not just the cache URL.
    if (queryString) {
      const updatedEndPoint = this.prepareUrlQueryString(endpointConfig.url, queryString);
      endpointConfig.url = updatedEndPoint;
    }
    if (cacheObj && !cacheObj.cacheKey) {
      cacheObj.cacheKey = this.prepareUrlQueryString(endpointConfig.url, queryParams);
    }
    const request: WorkerRequestArgs = this.populateWorkerArguments(headers, queryParams, cacheObj);
    const condition: boolean =
      SETTINGS.CHANNEL.toLocaleLowerCase() === API_GATEWAY_CONSTANTS.WEB ||
      SETTINGS.CHANNEL.toLocaleLowerCase() === API_GATEWAY_CONSTANTS.NRD;
    if (condition) {
      const method = 'GET';
      const paramUrl = this.configureURL(endpointConfig.url, pathParams);
      const partialToken: string = this.cryptoSignatureService.generatepartialToken(
        endpointConfig,
        null,
        method,
        request,
        pathParams,
        paramUrl,
        queryParams
      );
      return this.performSignatureandRequest(
        partialToken,
        request,
        method,
        endpointConfig,
        pathParams,
        critical,
        null,
        '',
        userFriendlyMessage
      );
    } else {
      return this.buildResponse(
        this.workerService.get(this.generateURL(endpointConfig, pathParams), request),
        endpointConfig,
        critical,
        { activityId: request.headers.get('activityId'), endUrl: endpointConfig.name },
        userFriendlyMessage
      );
    }
  }

  /**
   * Performs any type of http post request to the worker service. First argument is required,
   * Second argument is body of request and is mandatory
   * Second argument is optional, which is angular http Headers. THird parameter is object which is key value for
   * query params. Fourth argument is Object which is key value for path parameters in URL
   * Returns Observable for Response
   */
  /* tslint:disable*/
  public postRequest(
    endpointConfig: EndpointConfigInterface,
    payload: object,
    headers: HttpHeaders = null,
    queryParams: { [name: string]: string } = null,
    pathParams: { [name: string]: string } = null,
    cacheObj?: ApiCache,
    critical: boolean = true,
    userFriendlyMessage?: string
  ): Observable<HttpResponse<Object>> {
    const request: WorkerRequestArgs = this.populateWorkerArguments(headers, queryParams, cacheObj);
    const condition: boolean =
      SETTINGS.CHANNEL.toLocaleLowerCase() === API_GATEWAY_CONSTANTS.WEB ||
      SETTINGS.CHANNEL.toLocaleLowerCase() === API_GATEWAY_CONSTANTS.NRD;
    console.log(KEYPAIR);

    if (condition) {
      const method = 'POST';
      const paramUrl = this.configureURL(endpointConfig.url, pathParams);
      const partialToken: string = this.cryptoSignatureService.generatepartialToken(
        endpointConfig,
        payload ? JSON.stringify(payload) : '',
        method,
        request,
        pathParams,
        paramUrl,
        queryParams
      );
      return this.performSignatureandRequest(
        partialToken,
        request,
        method,
        endpointConfig,
        pathParams,
        critical,
        payload,
        '',
        userFriendlyMessage
      );
    } else {
      return this.buildResponse(
        this.workerService.post(this.generateURL(endpointConfig, pathParams), payload, request),
        endpointConfig,
        critical,
        { activityId: request.headers.get('activityId'), endUrl: endpointConfig.name },
        userFriendlyMessage
      );
    }
  }

  /**
   * Performs any type of http delete request to the worker service. First argument is required,
   * Second argument is optional, which is angular http Headers. THird parameter is object which is key value for
   * query params. Fourth argument is Object which is key value for path parameters in URL
   * Returns Observable for Response
   */
  public deleteRequest(
    endpointConfig: EndpointConfigInterface,
    payload: object,
    headers: HttpHeaders = null,
    queryParams: { [name: string]: string } = null,
    pathParams: { [name: string]: string } = null,
    critical: boolean = true,
    userFriendlyMessage?: string
  ): Observable<HttpResponse<Object>> {
    const request: WorkerRequestArgs = this.populateWorkerArguments(headers, queryParams);
    const condition: boolean =
      SETTINGS.CHANNEL.toLocaleLowerCase() === API_GATEWAY_CONSTANTS.WEB ||
      SETTINGS.CHANNEL.toLocaleLowerCase() === API_GATEWAY_CONSTANTS.NRD;
    if (condition) {
      const method = 'DELETE';
      const paramUrl = this.configureURL(endpointConfig.url, pathParams);
      const partialToken: string = this.cryptoSignatureService.generatepartialToken(
        endpointConfig,
        payload ? JSON.stringify(payload) : '',
        method,
        request,
        pathParams,
        paramUrl,
        queryParams
      );
      return this.performSignatureandRequest(
        partialToken,
        request,
        method,
        endpointConfig,
        pathParams,
        critical,
        payload,
        '',
        userFriendlyMessage
      );
    } else {
      return this.buildResponse(
        this.workerService.delete(this.generateURL(endpointConfig, pathParams), payload, request),
        endpointConfig,
        critical,
        { activityId: request.headers.get('activityId'), endUrl: endpointConfig.name },
        userFriendlyMessage
      );
    }
  }

  /**
   * Performs any type of http put request to the worker service. First argument is required,
   * Second argument is body of request and is mandatory
   * Second argument is optional, which is angular http Headers.
   * Third parameter is object which is key value for query params.
   * Fourth argument is Object which is key value for path parameters in URL
   * Returns Observable for Response
   */

  public putRequest(
    endpointConfig: EndpointConfigInterface,
    payload: object,
    headers: HttpHeaders = null,
    queryParams: { [name: string]: string } = null,
    pathParams: { [name: string]: string } = null,
    critical: boolean = true,
    userFriendlyMessage?: string
  ): Observable<HttpResponse<Object>> {
    const request: WorkerRequestArgs = this.populateWorkerArguments(headers, queryParams);
    const condition: boolean =
      SETTINGS.CHANNEL.toLocaleLowerCase() === API_GATEWAY_CONSTANTS.WEB ||
      SETTINGS.CHANNEL.toLocaleLowerCase() === API_GATEWAY_CONSTANTS.NRD;
    if (condition) {
      const method = 'PUT';
      const paramUrl = this.configureURL(endpointConfig.url, pathParams);
      const partialToken: string = this.cryptoSignatureService.generatepartialToken(
        endpointConfig,
        payload ? JSON.stringify(payload) : '',
        method,
        request,
        pathParams,
        paramUrl,
        queryParams
      );
      return this.performSignatureandRequest(
        partialToken,
        request,
        method,
        endpointConfig,
        pathParams,
        critical,
        payload,
        '',
        userFriendlyMessage
      );
    } else {
      return this.buildResponse(
        this.workerService.put(this.generateURL(endpointConfig, pathParams), payload, request),
        endpointConfig,
        critical,
        { activityId: request.headers.get('activityId'), endUrl: endpointConfig.name },
        userFriendlyMessage
      );
    }
  }

  public postEncodeRequest(
    endpointConfig: EndpointConfigInterface,
    payload: string,
    headers: HttpHeaders = null,
    queryParams: { [name: string]: string } = null,
    pathParams: { [name: string]: string } = null,
    critical: boolean = true
  ): Observable<HttpResponse<Object>> {
    const chb: CommonHeadersBuilder = new HeadersBuilderFactory(
      this.stateStoreUtil,
      this.commonUtil
    ).getHeaderbuilder(headers, SETTINGS.CHANNEL.toLocaleLowerCase());
    const requestHeaders: HttpHeaders = chb.getHeaders();
    const workerArg: WorkerRequestArgs = {
      headers: requestHeaders,
    };
    workerArg.headers = workerArg.headers
      .delete('Content-Type')
      .append('Content-Type', 'application/x-www-form-urlencoded');
    if (queryParams != null) {
      workerArg.params = queryParams;
    }

    const condition: boolean =
      SETTINGS.CHANNEL.toLocaleLowerCase() === API_GATEWAY_CONSTANTS.WEB ||
      SETTINGS.CHANNEL.toLocaleLowerCase() === API_GATEWAY_CONSTANTS.NRD;
    if (condition) {
      const paramUrl = this.configureURL(endpointConfig.url, pathParams);
      const method = 'POST-Encoded';
      const partialToken: string = this.cryptoSignatureService.generatepartialToken(
        endpointConfig,
        payload,
        'POST',
        workerArg,
        pathParams,
        paramUrl,
        queryParams
      );
      return this.performSignatureandRequest(
        partialToken,
        workerArg,
        method,
        endpointConfig,
        pathParams,
        critical,
        null,
        payload
      );
    }

    return this.buildResponse(
      this.workerService.post(this.generateURL(endpointConfig, pathParams), payload, workerArg),
      endpointConfig,
      critical
    );
  }

  public postEpayRequest(
    endpointConfig: EndpointConfigInterface,
    payload: string,
    headers: HttpHeaders = null,
    queryParams: { [name: string]: string } = null,
    pathParams: { [name: string]: string } = null,
    critical: boolean = true
  ): Observable<HttpResponse<Object>> {
    const chb: CommonHeadersBuilder = new HeadersBuilderFactory(
      this.stateStoreUtil,
      this.commonUtil
    ).getHeaderbuilder(headers, SETTINGS.CHANNEL.toLocaleLowerCase());
    const requestHeaders: HttpHeaders = new HttpHeaders();
    const workerArg: WorkerRequestArgs = {
      headers: requestHeaders,
    };
    workerArg.headers = workerArg.headers
      .delete('Content-Type')
      .append('Content-Type', 'application/x-www-form-urlencoded');
    return this.workerService.postEpayRequest(
      this.generateURL(endpointConfig, pathParams),
      payload,
      workerArg
    );
  }

  public resetSpinnerData(): void {
    this.spinnerOfDeathError = {};
    this.spinnerOfDeathCounter = {};
  }

  private generateURL(endpointConfig: EndpointConfigInterface, pathParams: {[name: string]: string }): string {
    let baseUrl = this.appConfig.getConfig(endpointConfig.domain);
    let path: string;
    if (endpointConfig.is_tag_proxy) {
      const proxyNameRegex = /{{PROXY_NAME}}/g;
      baseUrl = baseUrl.replace(proxyNameRegex, endpointConfig.proxyName);
      path = endpointConfig.url;
    } else {
      path = endpointConfig.proxyName 
        ? this.appConfig.getProxyUrl(endpointConfig.proxyName) + endpointConfig.url
        : endpointConfig.url;
    }
    this.incrementSpinnerCounter();
    baseUrl = baseUrl.replace(/\/$/, '');
    path = path.startsWith('/') ? path : '/' + path;
    const configuredPath = this.configureURL(`${path}`, pathParams);
    return `${baseUrl}${configuredPath}`;
  }

  private populateWorkerArguments(
    headers: HttpHeaders,
    queryParams: { [name: string]: string } = null,
    cacheConfig?: ApiCache
  ): WorkerRequestArgs {
    const chb: CommonHeadersBuilder = new HeadersBuilderFactory(
      this.stateStoreUtil,
      this.commonUtil
    ).getHeaderbuilder(headers, SETTINGS.CHANNEL.toLocaleLowerCase());
    const requestHeaders: HttpHeaders = chb.getHeaders();
    const workerArg: WorkerRequestArgs = Object.assign(
      {
        headers: requestHeaders,
      },
      (cacheConfig && {
        preferCache: cacheConfig.preferCache,
        cacheResponse: cacheConfig.cacheResponse,
        cacheKey: cacheConfig.cacheKey,
      }) ||
        {}
    );

    if (queryParams != null) {
      workerArg.params = queryParams;
    }
    return workerArg;
  }

  private configureURL(endPoint: string, pathParams: { [name: string]: string }): string {
    if (pathParams != null) {
      Object.keys(pathParams).map((key: string) => {
        endPoint = endPoint.replace('{' + key + '}', pathParams[key]);
        endPoint = endPoint.replace('[' + key + ']', pathParams[key]);
      });
    }
    endPoint = endPoint.replace(/(\[[^\[]*\])/g, ''); // Replace undefined optional param holders
    endPoint = endPoint.replace(/\/\//, '/'); // Replace '//' if any with '/'
    endPoint = endPoint.replace(/(\/)$/, ''); // Remove trailing '/'
    return endPoint;
  }

  private prepareUrlQueryString(endpoint: string, queryString: { [name: string]: string }): string {
    let appendToEndPoint = endpoint && endpoint.indexOf('?') >= 0 ? '&' : '?';
    if (queryString != null) {
      Object.keys(queryString).map((key: string) => {
        appendToEndPoint = appendToEndPoint
          .concat(encodeURIComponent(key))
          .concat('=')
          .concat(encodeURIComponent(queryString[key]));
        appendToEndPoint = appendToEndPoint.concat('&');
      });
    }
    return endpoint.concat(appendToEndPoint.substring(0, appendToEndPoint.length - 1));
  }

  /**
   * This is method is used to return proper response with error if occurs
   * @param {Observable<Response>} observableResponse
   * @returns {Observable<Response>}
   */
  private buildResponse(
    observableResponse: Observable<HttpResponse<Object>>,
    endpointConfig: EndpointConfigInterface,
    critical?: boolean,
    properties?: ApiProperties,
    userFriendlyMessage?: string
  ): Observable<HttpResponse<Object>> {
    return new Observable((observer: Subscriber<HttpResponse<Object>>) => {
      observableResponse.subscribe((response: any) => {
        if (response.ok) {
          if (endpointConfig.clearCache) {
            this.stateStoreUtil.setFreshDataAvailableFlag(true);
          }
          if (endpointConfig.storeActivityId) {
            this.stateStoreUtil.setActivityIdsforCacheClearing(properties.activityId);
          }
          this.checkDuplicateAPIErrors(endpointConfig);
          this.decrementSpinnerCounterAndRemove();
          this.setSeoMetadata(response.body);
          observer.next(response);
        } else {
          this.decrementSpinnerCounterAndRemove(true);
          if (this.isErrorHandlingEnabled() && critical) {
            this.httpEventInterceptorService.handleHttpError(
              this.updateResponseObject(response),
              endpointConfig.url,
              properties
            );
          }
          const errorResponse = this.objectService.get(response, 'body.errors.error');
          // tslint:disable-next-line:cyclomatic-complexity
          if (errorResponse) {
            errorResponse.forEach((error: APIError) => {
              if (userFriendlyMessage) {
                error.userMessage = userFriendlyMessage;
              }
              error.apiNumber = endpointConfig.name;
            });
            if (critical) {
              this.setGlobalError(errorResponse);
            }
          }
          observer.error(errorResponse);
        }
        observer.complete();
        return observer;
      });
    });
  }

  /**
   * This is a helper function to call seo service if
   * AEM Metadata data included in API response
   * (PDP Pages)
   */
  private setSeoMetadata(response: Content): void {
    const aemMetadata = response && response['model-view'];
    if (aemMetadata && aemMetadata.metadata && this.seoService && this.seoService.setSeoMetadata) {
      this.seoService.setSeoMetadata(aemMetadata);
    }
  }

  /**
   * This is method is used to return Facade endpoint from config
   * @returns string
   */
  private isErrorHandlingEnabled(): boolean {
    return this.appConfig.getConfig('isErrorHandlingEnabled');
  }

  /**
   * This is method is used to set the global error messages
   * @returns string
   */
  private setGlobalError(response: APIError[]): void {
    const errorResponse = response.find((errorObject: APIError) => {
      return errorObject.code === 'BIZ_0001_INVALID_TRANS_TYPE';
    });
    if (!errorResponse) {
      this.globalErrorService.setGlobalErrorMessage(response);
    }
  }
  /**
   * This is method is used to check duplicate global error messages
   * @returns void
   */
  private checkDuplicateAPIErrors(endpointConfig: EndpointConfigInterface): void {
    this.globalErrorService.checkDuplicateGlobalErrorMessage(endpointConfig);
  }

  /**
   * This is method is used to increment spinner count for APIs on the screen
   * @returns void
   */
  private incrementSpinnerCounter(): void {
    const route = window.location.pathname;
    if (this.spinnerOfDeathCounter && this.spinnerOfDeathCounter[route] !== undefined) {
      this.spinnerOfDeathCounter[route] += 1;
    } else {
      this.spinnerOfDeathCounter[route] = 1;
    }
  }

  /**
   * This is method is used to decrement spinner count for APIs on the screen
   * @param fromErrorCb
   * @returns void
   */
  private decrementSpinnerCounterAndRemove(fromErrorCb?: boolean): void {
    const route = window.location.pathname;
    if (this.spinnerOfDeathCounter && this.spinnerOfDeathCounter[route] !== undefined) {
      this.spinnerOfDeathCounter[route] -= 1;
      if (!this.spinnerOfDeathError[route]) {
        this.spinnerOfDeathError[route] = fromErrorCb;
      }
      if (this.spinnerOfDeathCounter[route] <= 0 && this.spinnerOfDeathError[route]) {
        this.removeSpinnerForRoute(route);
      }
    }
  }

  /**
   * This is method is used to remove spinner from the screen
   * @param route
   * @returns void
   */
  private removeSpinnerForRoute(route: string): void {
    const spinnerHandlerConfig = this.appConfig.getConfig('spinnerHandlerConfig');
    if (spinnerHandlerConfig && spinnerHandlerConfig.enabled) {
      this.globalErrorService.clearSpinner(route);
    }
  }

  /**
   * This is method used to reduce cyclomatic complexity.
   */
  private updateResponseObject(response: any): any {
    if (response.body && response.body.errors && response.body.errors.error) {
      response.error = this.objectService.get(response, 'body.errors.error');
    }
    return response;
  }

  private performSignatureandRequest(
    partialToken: string,
    request: WorkerRequestArgs,
    method: string,
    endpointConfig: EndpointConfigInterface,
    pathParams: { [name: string]: string } = null,
    critical: boolean,
    payload: object,
    encodedPost: string,
    userFriendlyMessage?: string
  ): Observable<HttpResponse<Object>> {
    return new Observable((observer: Subscriber<HttpResponse<Object>>) => {
      this.cryptoSignatureService
        .generatePopToken(partialToken)
        .pipe(
          map(
            (signature: ArrayBuffer) =>
              `${partialToken}.${Base64URL.stringify(new Uint8Array(signature))}`
          )
        )
        .subscribe((token: string) => {
          console.log('Token', token);
          //console.log('Pkey', this.objectService.get(self,'pKey.n'));
          request.headers = request.headers.set('x-authorization', token);
          if (endpointConfig.url === WEB_API_MAP.ANON_TOKEN_API_V6.url) {
            request.headers = request.headers.delete('Authorization');
            //interactionId screenId sessionId workflowId  on EMS/ APigees request removing following headers
            if (request.headers.get('interactionId')) {
              request.headers = request.headers.delete('interactionId');
            }
            if (request.headers.get('screenId')) {
              request.headers = request.headers.delete('screenId');
            }
            if (request.headers.get('sessionId')) {
              request.headers = request.headers.delete('sessionId');
            }
            if (request.headers.get('workflowId')) {
              request.headers = request.headers.delete('workflowId');
            }
          }
          //request['headers'].set('x-Pkey',  this.objectService.get(self,'pKey.n'));
          let callback: Observable<HttpResponse<Object>>;
          switch (method) {
            case 'POST':
              callback = this.buildResponse(
                this.workerService.post(
                  this.generateURL(endpointConfig, pathParams),
                  payload,
                  request
                ),
                endpointConfig,
                critical,
                { activityId: request.headers.get('activityId'), endUrl: endpointConfig.name },
                userFriendlyMessage
              );
              break;
            case 'GET':
              callback = this.buildResponse(
                this.workerService.get(this.generateURL(endpointConfig, pathParams), request),
                endpointConfig,
                critical,
                { activityId: request.headers.get('activityId'), endUrl: endpointConfig.name },
                userFriendlyMessage
              );
              break;
            case 'POST-Encoded':
              callback = this.buildResponse(
                this.workerService.post(
                  this.generateURL(endpointConfig, pathParams),
                  encodedPost,
                  request
                ),
                endpointConfig,
                critical
              );
              break;
            case 'DELETE':
              callback = this.buildResponse(
                this.workerService.delete(
                  this.generateURL(endpointConfig, pathParams),
                  payload,
                  request
                ),
                endpointConfig,
                critical,
                { activityId: request.headers.get('activityId'), endUrl: endpointConfig.name },
                userFriendlyMessage
              );
              break;
            case 'PUT':
              callback = this.buildResponse(
                this.workerService.put(
                  this.generateURL(endpointConfig, pathParams),
                  payload,
                  request
                ),
                endpointConfig,
                critical,
                { activityId: request.headers.get('activityId'), endUrl: endpointConfig.name },
                userFriendlyMessage
              );
              break;
          }
          callback.subscribe(
            (response) => {
              observer.next(response);
            },
            (error) => {
              observer.error(error);
            },
            () => {
              observer.complete();
              return observer;
            }
          );
        });
    });
  }
}
